import type { Inspection, InspectionSummary, FleetStats, ReportsSummary } from '@/types';
import { authFetch } from '@/utils/authFetch';

// ============================================================
// Wire3 Inspection API Client
// ============================================================

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json();
}

// --- Inspection Endpoints ---
export async function submitInspection(inspection: Inspection): Promise<{ id: string }> {
  return apiFetch('/inspections', {
    method: 'POST',
    body: JSON.stringify(inspection),
  });
}

export async function getInspection(id: string): Promise<Inspection> {
  return apiFetch(`/inspections/${id}`);
}

export async function getRecentInspections(limit = 20, skip = 0): Promise<{ items: InspectionSummary[]; total: number }> {
  return apiFetch(`/inspections?limit=${limit}&skip=${skip}&sort=-submittedAt`);
}

export async function getInspectionsByVehicle(vehicleId: string): Promise<InspectionSummary[]> {
  return apiFetch(`/inspections?vehicleId=${vehicleId}&sort=-submittedAt`);
}

export async function reviewInspection(id: string, reviewerId: string, notes?: string): Promise<void> {
  await apiFetch(`/inspections/${id}/review`, {
    method: 'POST',
    body: JSON.stringify({ reviewerId, notes }),
  });
}

export async function flagInspection(id: string, reason: string): Promise<void> {
  await apiFetch(`/inspections/${id}/flag`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// --- Fleet Stats ---
export async function getFleetStats(): Promise<FleetStats> {
  return apiFetch('/fleet/stats');
}

// --- Reports & Export ---
export async function getReportsSummary(start: string, end: string): Promise<ReportsSummary> {
  return apiFetch(`/reports/summary?start=${start}&end=${end}`);
}

export async function exportSubmissionsCsv(params: {
  start: string;
  end: string;
  vehicleId?: string;
  type?: string;
}): Promise<Blob> {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]
  );
  const res = await authFetch(`${BASE_URL}/inspections/export/submissions.csv?${query}`);
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
}

export async function exportMissedCsv(params: { start: string; end: string }): Promise<Blob> {
  const query = new URLSearchParams(params);
  const res = await authFetch(`${BASE_URL}/inspections/export/missed.csv?${query}`);
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
}

// --- Photo Upload ---
export async function uploadPhoto(inspectionId: string, checkId: string, file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('photo', file);
  formData.append('checkId', checkId);

  const res = await authFetch(`${BASE_URL}/inspections/${inspectionId}/photos`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  return res.json();
}